import React from 'react';
import {
  MessageCircle, Phone, Mail, MapPin, Instagram,
  Scissors, Palette, Shirt, Gift, Frame, Sparkles,
} from 'lucide-react';
import { STUDIO } from '../data/studio';

const DISCIPLINES = [
  { Icon: Scissors, label: 'Aari & Zardosi Embroidery' },
  { Icon: Palette, label: 'Resin & Canvas Paintings' },
  { Icon: Sparkles, label: 'Custom Designing' },
  { Icon: Gift, label: 'Trousseau Gift Hampers' },
  { Icon: Frame, label: '3D Floral Memory Frames' },
  { Icon: Shirt, label: 'Blouse & Master Tailoring' },
];

export default function Footer({ onOpenQuote, showTransformations }) {
  const handleWhatsApp = () => {
    const msg = encodeURIComponent(
      'Hello Handmade bee, I would like to ask about placing a custom order.'
    );
    window.open(`https://wa.me/${STUDIO.phone}?text=${msg}`, '_blank', 'noopener');
  };

  return (
    <footer className="bg-[#4E2C23] text-[#FFF7F0] pt-14 pb-10 border-t border-[#FFDAB9]/20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 pb-10 border-b border-white/15">

          {/* Brand */}
          <div className="lg:col-span-2 space-y-3.5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl overflow-hidden border border-[#FFDAB9]/40 shadow-sm bg-white">
                <img
                  src="/logo-128.webp"
                  alt=""
                  aria-hidden="true"
                  width="128"
                  height="128"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <span className="font-90s text-2xl font-black text-white block leading-none">
                  {STUDIO.name}
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#FFDAB9] font-bold block mt-0.5">
                  {STUDIO.tagline}
                </span>
              </div>
            </div>

            <p className="text-xs text-[#FFDAB9]/85 max-w-sm leading-relaxed">
              Hand embroidery, resin and canvas work, gift hampers, memory framing and
              tailoring — made to order, one piece at a time.
            </p>

            <div className="flex items-center gap-2.5 pt-1">
              <button
                onClick={handleWhatsApp}
                aria-label="Message the studio on WhatsApp"
                className="p-2.5 rounded-full bg-[#E2725B] text-white hover:bg-[#F08E7A] transition-all shadow-sm"
              >
                <MessageCircle className="w-4 h-4" />
              </button>

              {STUDIO.instagram && (
                <a
                  href={STUDIO.instagram}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Handmade bee on Instagram"
                  className="p-2.5 rounded-full bg-white/10 text-white hover:bg-[#E2725B] transition-all shadow-sm"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              )}

              <a
                href={`tel:+${STUDIO.phone}`}
                aria-label={`Call the studio on ${STUDIO.phoneDisplay}`}
                className="p-2.5 rounded-full bg-white/10 text-white hover:bg-[#E2725B] transition-all shadow-sm"
              >
                <Phone className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Disciplines */}
          <div className="space-y-2.5">
            <h4 className="text-xs uppercase font-extrabold tracking-widest text-[#FFDAB9]">
              What We Make
            </h4>
            <ul className="space-y-2 text-xs text-[#FFF7F0]/80">
              {DISCIPLINES.map(({ Icon, label }) => (
                <li key={label}>
                  <a href="#showcase" className="hover:text-[#FFDAB9] transition-colors flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-[#FFDAB9]/70 flex-shrink-0" strokeWidth={1.7} />
                    <span>{label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Navigation + contact */}
          <div className="space-y-2.5">
            <h4 className="text-xs uppercase font-extrabold tracking-widest text-[#FFDAB9]">
              Get In Touch
            </h4>
            <ul className="space-y-2 text-xs text-[#FFF7F0]/80">
              <li>
                <a href="#showcase" className="hover:text-[#FFDAB9] transition-colors">
                  Portfolio
                </a>
              </li>
              {showTransformations && (
                <li>
                  <a href="#transformations" className="hover:text-[#FFDAB9] transition-colors">
                    Before &amp; After
                  </a>
                </li>
              )}
              <li>
                <a href="#questions" className="hover:text-[#FFDAB9] transition-colors">
                  Questions
                </a>
              </li>
              <li>
                <button
                  onClick={onOpenQuote}
                  className="hover:text-[#FFDAB9] transition-colors text-left"
                >
                  Start an Order
                </button>
              </li>
            </ul>

            <div className="space-y-2 text-xs text-[#FFF7F0]/80 pt-2">
              <a
                href={`tel:+${STUDIO.phone}`}
                className="flex items-center gap-2 hover:text-[#FFDAB9] transition-colors"
              >
                <Phone className="w-3.5 h-3.5 text-[#FFDAB9] flex-shrink-0" />
                <span className="tabular-nums">{STUDIO.phoneDisplay}</span>
              </a>

              {STUDIO.email && (
                <a
                  href={`mailto:${STUDIO.email}`}
                  className="flex items-center gap-2 hover:text-[#FFDAB9] transition-colors"
                >
                  <Mail className="w-3.5 h-3.5 text-[#FFDAB9] flex-shrink-0" />
                  <span>{STUDIO.email}</span>
                </a>
              )}

              {STUDIO.place && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 text-[#FFDAB9] flex-shrink-0 mt-0.5" />
                  <span>{STUDIO.place.display}</span>
                </div>
              )}

              {STUDIO.hours && (
                <div className="text-[10px] text-[#FFDAB9] font-bold pt-1">{STUDIO.hours}</div>
              )}
            </div>
          </div>

        </div>

        <div className="pt-6 text-xs text-[#FFDAB9]/70">
          © {new Date().getFullYear()} {STUDIO.name}. Every piece made by hand, to order.
        </div>
      </div>
    </footer>
  );
}
